import { useState, useCallback, useRef, createContext, useContext } from 'react';
import axios from 'axios';
import { baseMakeRequest, getBucket, networkGlobals } from 'utils/networkUtils';
import { Brand } from 'models/BO/Brand';
import { Language } from 'models/content/Language';
import { translationsLanguagesDO } from 'utils/translateUtils';
export interface TranslationsState {
  translations: { [brandName: string]: any };
  isLoading: boolean;
  error: string | null;
  currentLanguage: string;
  languages: Language[];
}

export interface UseTranslationsReturn extends TranslationsState {
  loadTranslations: (brand: Brand) => Promise<any>;
  tr: (key: string, args?: { [key: string]: string }) => string;
  setCurrentLanguage: (language: string) => void;
  loadLanguages: () => Promise<void>;
}

export const TranslationsContext = createContext<UseTranslationsReturn>({
  translations: {},
  isLoading: false,
  error: null,
  loadTranslations: () => Promise.resolve({}),
  tr: () => '',
  currentLanguage: 'en',
  languages: [],
  setCurrentLanguage: () => {},
  loadLanguages: () => Promise.resolve(),
});

/**
 * Hook for managing translations across different brands
 * 
 * @returns {UseTranslationsReturn} Object containing translation state and functions
 * 
 * @example
 * ```tsx
 * const { translations, isLoading, error, loadTranslations, tr } = useTranslations();
 * 
 * // Load translations for a brand
 * await loadTranslations(brand);
 * 
 * // Use translations
 * const translatedText = tr('welcome_message', { name: 'John' });
 * ```
 */
export const useTranslations = (): UseTranslationsReturn => {
  const {
    translations,
    isLoading,
    error,
    loadTranslations,
    tr,
    currentLanguage,
    languages,
    setCurrentLanguage,
    loadLanguages,
  } = useContext<UseTranslationsReturn>(TranslationsContext);

  return {
    translations,
    isLoading,
    error,
    loadTranslations,
    tr,
    currentLanguage,
    languages,
    setCurrentLanguage,
    loadLanguages,
  };
};

export function TranslationsProvider({ children }) {
  const [state, setState] = useState<TranslationsState>({
    translations: {},
    isLoading: false,
    error: null,
    currentLanguage: 'en',
    languages: [],
  });

  const loadingBrands = useRef<Set<string>>(new Set());

  /**
   * Load translations for a specific brand
   * 
   * @param {Brand} brand - The brand to load translations for
   * @returns {Promise<any>} Promise that resolves to the loaded translations
   * 
   * @example
   * ```tsx
   * const { loadTranslations } = useTranslations();
   * 
   * try {
   *   const translations = await loadTranslations(brand);
   *   console.log('Translations loaded:', translations);
   * } catch (error) {
   *   console.error('Failed to load translations:', error);
   * }
   * ```
   */
  const loadTranslations = useCallback(async (brand: Brand): Promise<any> => {
    if (!brand) {
      throw new Error('Brand is required to load translations');
    }

    // Prevent loading translations for the same brand multiple times
    if (state.translations[brand.name] || loadingBrands.current.has(brand.name)) {
      return state.translations[brand.name];
    }

    try {
      loadingBrands.current.add(brand.name);
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const response = await axios.get(
        `https://${getBucket()}.s3.amazonaws.com/public/generated/${brand.folder}/4/en/translations.json`
      );

      const translations = response.data;
      
      setState(prev => ({
        ...prev,
        translations: {
          ...prev.translations,
          [brand.name]: translations
        },
        isLoading: false
      }));

      console.log(`Translations loaded for brand: ${brand.name}`);
      return translations;
    } catch (error) {
      console.error('Error loading translations:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load translations'
      }));
      throw error;
    } finally {
      loadingBrands.current.delete(brand.name);
    }
  }, [state.translations]);

  /**
   * Translate a key to its localized text
   * 
   * @param {string} key - The translation key
   * @param {Object} args - Optional arguments to replace placeholders in the translation
   * @returns {string} The translated text or the original key if translation not found
   * 
   * @example
   * ```tsx
   * const { tr } = useTranslations();
   * 
   * // Simple translation
   * const welcome = tr('welcome_message');
   * 
   * // Translation with placeholders
   * const greeting = tr('hello_user', { name: 'John', time: 'morning' });
   * ```
   */
  const tr = useCallback((key: string, args: { [key: string]: string } = {}): string => {
    if (!key) return '';
    
    const currentBrandName = networkGlobals.brand?.name;
    if (!currentBrandName) return key;

    const brandTranslations = state.translations[currentBrandName];
    if (!brandTranslations) return key;

    // Try exact key first, then lowercase
    const text = brandTranslations[key] || brandTranslations[key?.toLowerCase()] || key;
    
    // Replace placeholders with arguments
    return text?.replace(/@(\w+)/g, (match, capture) => args[capture] || match);
  }, [state.translations]);

  const setCurrentLanguage = useCallback((language: string) => {
    networkGlobals.langauge = language;
    setState(prev => ({ ...prev, currentLanguage: language }));
  }, []);

  const loadLanguages = useCallback(async () => {
    translationsLanguagesDO().then((res) => {
      setState(prev => ({ ...prev, languages: res }));
    });
  }, []);

  return (
    <TranslationsContext.Provider value={{
      ...state,
      loadTranslations,
      tr,
      setCurrentLanguage,
      loadLanguages,
    }}>
      {children}
    </TranslationsContext.Provider>
  );
} 